import { GolfPropertyEnum } from "../../constants/NavbarList";
import { ExcelExportSheetConfig } from "../types";
import {
  GolfExcelHeader,
  GolfExcelSheetName,
  GolfExcelSourceField,
} from "../enums/golfExcel";
import {
  golfExcelColumn,
  golfExcelFile,
  golfExcelSheet,
  golfExcelWorksheet,
} from "../enums/typedExcel";
import {
  EXCEL_DEFAULT_START_DATE,
  EXCEL_DEFAULT_END_DATE_PLUS_3_YEARS,
  EXCEL_DEFAULT_END_DATE,
} from "../constants/dateDefaults";
import {
  ExcelBoolean,
  ExcelBooleanString,
  ExcelEmptyDefault,
} from "../constants/defaults";
import { DEFAULT_VENDOR_CONTACT_TYPE } from "../../constants/VendorContactTypes";
import {
  mapPlayerTypeMappingRows,
  mapRateTypeMappingRows,
} from "../mapping/golfMappingRows";
import {
  mapRateTypeDaysOutRows,
  mapRateTypeExportRows,
} from "../mapping/golfRateTypeRows";
import {
  mapVendorSetupContactRows,
  mapVendorSetupVendorRows,
} from "../mapping/vendorSetupRows";
import { DEFAULT_RETAIL_ITEM_NUMBER } from "../../constants/DefaultRetailItems";
import {
  mapRetailItemRows,
  retailItemExcelColumns,
} from "../mapping/retailItemRows";

/**
 * Golf Excel export definitions — mapped to Golf onboarding payload keys.
 * Column headers follow the import template spec; sourceField maps onboarding data.
 */
export const golfExcelExportSheets: ExcelExportSheetConfig[] = [
  golfExcelSheet(GolfPropertyEnum.outletSetup, GolfExcelSheetName.Outlet, [
    golfExcelColumn(
      GolfExcelHeader.Id,
      ExcelEmptyDefault.Empty,
      ExcelEmptyDefault.Empty,
    ),
    golfExcelColumn(
      GolfExcelHeader.OutletName,
      GolfExcelSourceField.OutletName,
    ),
    golfExcelColumn(
      GolfExcelHeader.ProfitCenter,
      ExcelEmptyDefault.Empty,
      ExcelEmptyDefault.Empty,
    ),
    golfExcelColumn(
      GolfExcelHeader.IsActive,
      GolfExcelSourceField.Active,
      ExcelBooleanString.True,
    ),
    golfExcelColumn(
      GolfExcelHeader.TerminalId,
      ExcelEmptyDefault.Empty,
      ExcelEmptyDefault.Empty,
    ),
    golfExcelColumn(
      GolfExcelHeader.AutoReceipt,
      GolfExcelSourceField.AutoPrintsReceipts,
      ExcelBooleanString.True,
    ),
  ]),

  golfExcelSheet(
    GolfPropertyEnum.retailCategories,
    GolfExcelSheetName.Category,
    [
      golfExcelColumn(
        GolfExcelHeader.Id,
        ExcelEmptyDefault.Empty,
        ExcelEmptyDefault.Empty,
      ),
      golfExcelColumn(GolfExcelHeader.Name, GolfExcelSourceField.CategoryName),
      golfExcelColumn(
        GolfExcelHeader.EligibleForDiscount,
        GolfExcelSourceField.EligibleForDiscount,
        ExcelBooleanString.False,
      ),
      golfExcelColumn(
        GolfExcelHeader.IsActive,
        GolfExcelSourceField.Active,
        ExcelBoolean.True,
      ),
      golfExcelColumn(
        GolfExcelHeader.CategoryGroupName,
        GolfExcelSourceField.CategoryGroup,
        ExcelEmptyDefault.Empty,
      ),
    ],
  ),

  golfExcelSheet(
    GolfPropertyEnum.retailItems,
    GolfExcelSheetName.RetailItem,
    retailItemExcelColumns,
    GolfExcelSheetName.RetailItem,
    mapRetailItemRows,
  ),

  golfExcelSheet(GolfPropertyEnum.playerTypes, GolfExcelSheetName.PlayerType, [
    golfExcelColumn(
      GolfExcelHeader.Id,
      ExcelEmptyDefault.Empty,
      ExcelEmptyDefault.Empty,
    ),
    golfExcelColumn(GolfExcelHeader.Type, GolfExcelSourceField.PlayerType),
    golfExcelColumn(
      GolfExcelHeader.GuaranteeType,
      GolfExcelSourceField.Guarantee,
      "None",
    ),
    golfExcelColumn(
      GolfExcelHeader.DaysOutStart,
      GolfExcelSourceField.DaysOutStart2,
      0,
    ),
    golfExcelColumn(
      GolfExcelHeader.DaysOutEnd,
      GolfExcelSourceField.DaysOutEnd,
      30,
    ),
    golfExcelColumn(
      GolfExcelHeader.IsAvailableOnWeb,
      GolfExcelSourceField.AvailableOnWeb,
      ExcelBoolean.False,
    ),
    golfExcelColumn(
      GolfExcelHeader.IsActive,
      GolfExcelSourceField.Active,
      ExcelBoolean.True,
    ),
  ]),

  golfExcelFile(GolfPropertyEnum.rateTypes, GolfExcelSheetName.RateType, [
    golfExcelWorksheet(
      GolfExcelSheetName.RateType,
      [
        golfExcelColumn(
          GolfExcelHeader.Id,
          ExcelEmptyDefault.Empty,
          ExcelEmptyDefault.Empty,
        ),
        golfExcelColumn(GolfExcelHeader.Type, GolfExcelSourceField.RateType),
        golfExcelColumn(
          GolfExcelHeader.GuaranteeType,
          GolfExcelSourceField.Guarantee,
          "None",
        ),
        golfExcelColumn(
          GolfExcelHeader.IsAvailableOnWeb,
          GolfExcelSourceField.AvailableOnWeb,
          ExcelBoolean.False,
        ),
        golfExcelColumn(
          GolfExcelHeader.IsSupressReceipt,
          GolfExcelSourceField.SuppressOnReceipt,
          ExcelBoolean.False,
        ),
        golfExcelColumn(
          GolfExcelHeader.IsActive,
          GolfExcelSourceField.Active,
          ExcelBoolean.True,
        ),
        golfExcelColumn(
          GolfExcelHeader.IsNineHoles,
          GolfExcelSourceField.N9Hole,
          ExcelBoolean.False,
        ),
      ],
      ExcelBoolean.False,
      mapRateTypeExportRows,
    ),
    golfExcelWorksheet(
      GolfExcelSheetName.RateTypeDaysOut,
      [
        golfExcelColumn(GolfExcelHeader.Type, GolfExcelSourceField.RateType),
        golfExcelColumn(
          GolfExcelHeader.DaysOutStart,
          GolfExcelSourceField.DaysOutStart,
          0,
        ),
        golfExcelColumn(
          GolfExcelHeader.DaysOutEnd,
          GolfExcelSourceField.DaysOutEnd,
          30,
        ),
        golfExcelColumn(
          GolfExcelHeader.MinimumPlayers,
          GolfExcelSourceField.MinNumberPlayers,
          0,
        ),
      ],
      ExcelBoolean.False,
      mapRateTypeDaysOutRows,
    ),
  ]),

  golfExcelSheet(
    GolfPropertyEnum.lessonLocation,
    GolfExcelSheetName.LessonLocation,
    [
      golfExcelColumn(
        GolfExcelHeader.Id,
        ExcelEmptyDefault.Empty,
        ExcelEmptyDefault.Empty,
      ),
      golfExcelColumn(
        GolfExcelHeader.Location,
        GolfExcelSourceField.LessonLocations,
      ),
      golfExcelColumn(
        GolfExcelHeader.IsActive,
        ExcelEmptyDefault.Empty,
        ExcelBoolean.True,
      ),
    ],
  ),

  golfExcelSheet(
    GolfPropertyEnum.allocationBlocks,
    GolfExcelSheetName.AllocationBlock,
    [
      golfExcelColumn(
        GolfExcelHeader.Id,
        ExcelEmptyDefault.Empty,
        ExcelEmptyDefault.Empty,
      ),
      golfExcelColumn(
        GolfExcelHeader.AllocationBlockCode,
        GolfExcelSourceField.AllocationBlockCode,
      ),
      golfExcelColumn(
        GolfExcelHeader.AllocationBlockName,
        GolfExcelSourceField.AllocationBlockName,
      ),
      golfExcelColumn(
        GolfExcelHeader.ColorCode,
        GolfExcelSourceField.ColorOptions,
        "0xFF0000",
      ),
      golfExcelColumn(GolfExcelHeader.DaysOut, GolfExcelSourceField.DaysOut, 0),
      golfExcelColumn(
        GolfExcelHeader.SwitchToAllocationBlockName,
        GolfExcelSourceField.SwitchTo,
        ExcelEmptyDefault.Empty,
      ),
      golfExcelColumn(
        GolfExcelHeader.SwitchTime,
        ExcelEmptyDefault.Empty,
        ExcelEmptyDefault.Empty,
      ),
      golfExcelColumn(
        GolfExcelHeader.Closed,
        GolfExcelSourceField.ClosedQ,
        ExcelBoolean.False,
      ),
      golfExcelColumn(
        GolfExcelHeader.DisplayOnTeesheet,
        GolfExcelSourceField.ShowOnTeeSheet,
        ExcelBoolean.True,
      ),
      golfExcelColumn(
        GolfExcelHeader.IsActive,
        GolfExcelSourceField.Active,
        ExcelBoolean.True,
      ),
    ],
  ),

  golfExcelSheet(
    GolfPropertyEnum.cancelReasons,
    GolfExcelSheetName.CancellationReasons,
    [
      golfExcelColumn(
        GolfExcelHeader.Id,
        ExcelEmptyDefault.Empty,
        ExcelEmptyDefault.Empty,
      ),
      golfExcelColumn(
        GolfExcelHeader.CancelReason,
        GolfExcelSourceField.CancelReasons,
      ),
      golfExcelColumn(
        GolfExcelHeader.IsDefault,
        GolfExcelSourceField.Default,
        ExcelBoolean.False,
      ),
      golfExcelColumn(
        GolfExcelHeader.IsActive,
        ExcelEmptyDefault.Empty,
        ExcelBooleanString.True,
      ),
    ],
  ),

  golfExcelSheet(
    GolfPropertyEnum.discountTypes,
    GolfExcelSheetName.DiscountTypes,
    [
      golfExcelColumn(
        GolfExcelHeader.Id,
        ExcelEmptyDefault.Empty,
        ExcelEmptyDefault.Empty,
      ),
      golfExcelColumn(
        GolfExcelHeader.DiscountType,
        GolfExcelSourceField.DiscountType,
      ),
      golfExcelColumn(
        GolfExcelHeader.IsActive,
        GolfExcelSourceField.Active,
        ExcelBoolean.True,
      ),
    ],
  ),

  golfExcelSheet(
    GolfPropertyEnum.categoryGroups,
    GolfExcelSheetName.CategoryGroup,
    [
      golfExcelColumn(
        GolfExcelHeader.Id,
        ExcelEmptyDefault.Empty,
        ExcelEmptyDefault.Empty,
      ),
      golfExcelColumn(GolfExcelHeader.Name, GolfExcelSourceField.CategoryGroup),
      golfExcelColumn(
        GolfExcelHeader.IsActive,
        GolfExcelSourceField.Active,
        ExcelBoolean.True,
      ),
    ],
  ),

  golfExcelSheet(
    GolfPropertyEnum.discountReasons,
    GolfExcelSheetName.DiscountReasons,
    [
      golfExcelColumn(
        GolfExcelHeader.Id,
        ExcelEmptyDefault.Empty,
        ExcelEmptyDefault.Empty,
      ),
      golfExcelColumn(
        GolfExcelHeader.DiscountReason,
        GolfExcelSourceField.DiscountReason,
      ),
      golfExcelColumn(
        GolfExcelHeader.IsActive,
        GolfExcelSourceField.Active,
        ExcelBoolean.True,
      ),
    ],
  ),

  golfExcelSheet(
    GolfPropertyEnum.quickSaleCategories,
    GolfExcelSheetName.QuickSaleCategory,
    [
      golfExcelColumn(
        GolfExcelHeader.Id,
        ExcelEmptyDefault.Empty,
        ExcelEmptyDefault.Empty,
      ),
      golfExcelColumn(
        GolfExcelHeader.Category,
        GolfExcelSourceField.QuickSaleCategory,
      ),
      golfExcelColumn(
        GolfExcelHeader.IsActive,
        GolfExcelSourceField.Active,
        ExcelBoolean.True,
      ),
      golfExcelColumn(
        GolfExcelHeader.Outlets,
        GolfExcelSourceField.OutletName,
        ExcelEmptyDefault.Empty,
      ),
    ],
  ),

  golfExcelSheet(GolfPropertyEnum.taxes, GolfExcelSheetName.Tax, [
    golfExcelColumn(
      GolfExcelHeader.Id,
      ExcelEmptyDefault.Empty,
      ExcelEmptyDefault.Empty,
    ),
    golfExcelColumn(GolfExcelHeader.OutletName, GolfExcelSourceField.Outlet),
    golfExcelColumn(GolfExcelHeader.TaxName, GolfExcelSourceField.TaxName),
    golfExcelColumn(GolfExcelHeader.TaxValue, GolfExcelSourceField.Value, 0),
    golfExcelColumn(
      GolfExcelHeader.AllowTaxOnGratuity,
      GolfExcelSourceField.AllowTaxOnGratuity,
      ExcelBooleanString.False,
    ),
    golfExcelColumn(
      GolfExcelHeader.AllowTaxOnServiceCharge,
      GolfExcelSourceField.AllowTaxOnServiceCharge,
      ExcelBooleanString.False,
    ),
    golfExcelColumn(
      GolfExcelHeader.IsDefaultTax,
      GolfExcelSourceField.DefaultToAllRetailItems,
      ExcelBooleanString.False,
    ),
    golfExcelColumn(
      GolfExcelHeader.IsAmountOrPercentage,
      GolfExcelSourceField.PercentageOrFlatAmount,
      ExcelBoolean.True,
    ),
    golfExcelColumn(GolfExcelHeader.TaxAmount, GolfExcelSourceField.Value),
    golfExcelColumn(
      GolfExcelHeader.StartDate2,
      GolfExcelSourceField.StartDate,
      EXCEL_DEFAULT_START_DATE,
    ),
    golfExcelColumn(
      GolfExcelHeader.EndDate2,
      GolfExcelSourceField.EndDate,
      EXCEL_DEFAULT_END_DATE_PLUS_3_YEARS,
    ),
  ]),

  golfExcelSheet(GolfPropertyEnum.vendorTypes, GolfExcelSheetName.VendorType, [
    golfExcelColumn(
      GolfExcelHeader.Id,
      ExcelEmptyDefault.Empty,
      ExcelEmptyDefault.Empty,
    ),
    golfExcelColumn(
      GolfExcelHeader.VendorType,
      GolfExcelSourceField.VendorType,
    ),
    golfExcelColumn(
      GolfExcelHeader.IsActive,
      ExcelEmptyDefault.Empty,
      ExcelBooleanString.True,
    ),
  ]),

  golfExcelFile(GolfPropertyEnum.vendorSetup, GolfExcelSheetName.Vendor, [
    golfExcelWorksheet(
      GolfExcelSheetName.Vendor,
      [
        golfExcelColumn(
          GolfExcelHeader.Id,
          ExcelEmptyDefault.Empty,
          ExcelEmptyDefault.Empty,
        ),
        golfExcelColumn(
          GolfExcelHeader.VendorCode,
          GolfExcelSourceField.VendorCode,
        ),
        golfExcelColumn(
          GolfExcelHeader.VendorName,
          GolfExcelSourceField.VendorName,
        ),
        golfExcelColumn(
          GolfExcelHeader.VendorType,
          GolfExcelSourceField.VendorType,
          ExcelEmptyDefault.Empty,
        ),
        golfExcelColumn(
          GolfExcelHeader.IsActive,
          ExcelEmptyDefault.Empty,
          ExcelBooleanString.True,
        ),
        golfExcelColumn(
          GolfExcelHeader.RepresentativeFname,
          GolfExcelSourceField.FirstName,
        ),
        golfExcelColumn(
          GolfExcelHeader.RepresentativeLname,
          GolfExcelSourceField.LastName,
        ),
        golfExcelColumn(
          GolfExcelHeader.Comments,
          GolfExcelSourceField.Comments,
          ExcelEmptyDefault.Empty,
        ),
        golfExcelColumn(
          GolfExcelHeader.AddressLine1,
          GolfExcelSourceField.Address,
        ),
        golfExcelColumn(
          GolfExcelHeader.AddressLine2,
          ExcelEmptyDefault.Empty,
          ExcelEmptyDefault.Empty,
        ),
        golfExcelColumn(
          GolfExcelHeader.AddressLine3,
          ExcelEmptyDefault.Empty,
          ExcelEmptyDefault.Empty,
        ),
        golfExcelColumn(GolfExcelHeader.City, GolfExcelSourceField.City),
        golfExcelColumn(GolfExcelHeader.State, GolfExcelSourceField.State),
        golfExcelColumn(GolfExcelHeader.Country, GolfExcelSourceField.Country),
        golfExcelColumn(
          GolfExcelHeader.PostalCode,
          GolfExcelSourceField.PostalCode,
        ),
        golfExcelColumn(
          GolfExcelHeader.Terms,
          ExcelEmptyDefault.Empty,
          ExcelEmptyDefault.Empty,
        ),
      ],
      ExcelBoolean.False,
      mapVendorSetupVendorRows,
    ),
    golfExcelWorksheet(
      GolfExcelSheetName.VendorContactInformation,
      [
        golfExcelColumn(
          GolfExcelHeader.VendorCode,
          GolfExcelSourceField.VendorCode,
        ),
        golfExcelColumn(
          GolfExcelHeader.IsPrimary,
          GolfExcelSourceField.IsPrimary,
          ExcelBooleanString.True,
        ),
        golfExcelColumn(
          GolfExcelHeader.IsPrivateInfo,
          GolfExcelSourceField.IsPrivateInfo,
          ExcelBooleanString.False,
        ),
        golfExcelColumn(
          GolfExcelHeader.ContactType,
          GolfExcelSourceField.ContactType,
          DEFAULT_VENDOR_CONTACT_TYPE,
        ),
        golfExcelColumn(GolfExcelHeader.Value, GolfExcelSourceField.Value),
      ],
      ExcelBoolean.False,
      mapVendorSetupContactRows,
    ),
  ]),

  golfExcelSheet(GolfPropertyEnum.courses, GolfExcelSheetName.Course, [
    golfExcelColumn(
      GolfExcelHeader.Id,
      ExcelEmptyDefault.Empty,
      ExcelEmptyDefault.Empty,
    ),
    golfExcelColumn(
      GolfExcelHeader.CourseName,
      GolfExcelSourceField.CourseName,
    ),
    golfExcelColumn(
      GolfExcelHeader.AvgPlay9Holes,
      GolfExcelSourceField.DurationOfPlay9Holes,
      ExcelEmptyDefault.Empty,
    ),
    golfExcelColumn(
      GolfExcelHeader.AvgPlay18Holes,
      GolfExcelSourceField.DurationOfPlay18Holes,
      ExcelEmptyDefault.Empty,
    ),
    golfExcelColumn(
      GolfExcelHeader.IsActive,
      ExcelEmptyDefault.Empty,
      ExcelBooleanString.True,
    ),
    golfExcelColumn(
      GolfExcelHeader.ResvRqd,
      ExcelEmptyDefault.Empty,
      ExcelBooleanString.False,
    ),
    golfExcelColumn(
      GolfExcelHeader.OutletName,
      GolfExcelSourceField.DefaultOutlet,
    ),
  ]),

  golfExcelSheet(
    GolfPropertyEnum.teeTimeIntervalTemplates,
    GolfExcelSheetName.TeeTimeIntervalTemplates,
    [
      golfExcelColumn(
        GolfExcelHeader.TemplateCode,
        GolfExcelSourceField.TemplateCode,
      ),
      golfExcelColumn(
        GolfExcelHeader.TemplateName,
        GolfExcelSourceField.TemplateName,
      ),
      golfExcelColumn(
        GolfExcelHeader.StartTime,
        GolfExcelSourceField.StartTime,
      ),
      golfExcelColumn(GolfExcelHeader.EndTime, GolfExcelSourceField.EndTime),
      golfExcelColumn(
        GolfExcelHeader.IntervalMinutes,
        GolfExcelSourceField.IntervalMinutes,
      ),
    ],
  ),

  golfExcelSheet(
    GolfPropertyEnum.teeTimeIntervalSetup,
    GolfExcelSheetName.TeeTimeIntervalSetup,
    [
      golfExcelColumn(
        GolfExcelHeader.StartDate,
        GolfExcelSourceField.StartDate,
        EXCEL_DEFAULT_START_DATE,
      ),
      golfExcelColumn(
        GolfExcelHeader.EndDate,
        GolfExcelSourceField.EndDate,
        EXCEL_DEFAULT_END_DATE,
      ),
      golfExcelColumn(
        GolfExcelHeader.StartTime,
        GolfExcelSourceField.StartTime,
      ),
      golfExcelColumn(GolfExcelHeader.EndTime, GolfExcelSourceField.EndTime),
      golfExcelColumn(GolfExcelHeader.Course, GolfExcelSourceField.Course),
      golfExcelColumn(
        GolfExcelHeader.NumberOfMinutes,
        GolfExcelSourceField.NumberOfMinutes,
        ExcelEmptyDefault.Empty,
      ),
      golfExcelColumn(
        GolfExcelHeader.Time,
        GolfExcelSourceField.Time,
        ExcelEmptyDefault.Empty,
      ),
      golfExcelColumn(
        GolfExcelHeader.ShowOnTeeSheet,
        GolfExcelSourceField.ShowOnTeeSheet,
        ExcelBoolean.False,
      ),
      golfExcelColumn(
        GolfExcelHeader.ClosedQ,
        GolfExcelSourceField.ClosedQ,
        ExcelBoolean.False,
      ),
      golfExcelColumn(
        GolfExcelHeader.AvailableDaysOfWeek,
        GolfExcelSourceField.AvailableDaysOfWeek,
      ),
    ],
  ),

  golfExcelSheet(GolfPropertyEnum.rateSetup, GolfExcelSheetName.RatesSetup, [
    golfExcelColumn(GolfExcelHeader.Course, GolfExcelSourceField.Course),
    golfExcelColumn(GolfExcelHeader.RateType, GolfExcelSourceField.RateType),
    golfExcelColumn(
      GolfExcelHeader.GreenFeeAmountDollars,
      GolfExcelSourceField.GreenFeeAmountDollars,
    ),
    golfExcelColumn(
      GolfExcelHeader.GreenFeeItemNumber,
      GolfExcelSourceField.GreenFeeItemNumber,
      DEFAULT_RETAIL_ITEM_NUMBER,
    ),
    golfExcelColumn(
      GolfExcelHeader.CartFeeAmountDollars,
      GolfExcelSourceField.CartFeeAmountDollars,
    ),
    golfExcelColumn(
      GolfExcelHeader.CartFeeItemNumber,
      GolfExcelSourceField.CartFeeItemNumber,
      DEFAULT_RETAIL_ITEM_NUMBER,
    ),
    golfExcelColumn(
      GolfExcelHeader.StartDate,
      GolfExcelSourceField.StartDate,
      EXCEL_DEFAULT_START_DATE,
    ),
    golfExcelColumn(
      GolfExcelHeader.EndDate,
      GolfExcelSourceField.EndDate,
      EXCEL_DEFAULT_END_DATE_PLUS_3_YEARS,
    ),
    golfExcelColumn(GolfExcelHeader.StartTime, GolfExcelSourceField.StartTime),
    golfExcelColumn(GolfExcelHeader.EndTime, GolfExcelSourceField.EndTime),
    golfExcelColumn(
      GolfExcelHeader.AvailableDaysOfTheWeek,
      GolfExcelSourceField.AvailableDaysOfTheWeek,
    ),
  ]),

  golfExcelSheet(
    GolfPropertyEnum.playerRateAllocation,
    GolfExcelSheetName.RateTypeMapping,
    [
      golfExcelColumn(
        GolfExcelHeader.PlayerType,
        GolfExcelSourceField.PlayerType,
      ),
      golfExcelColumn(GolfExcelHeader.RateTypes, GolfExcelSourceField.RateType),
    ],
    GolfExcelSheetName.RateTypeMapping,
    mapRateTypeMappingRows,
  ),

  golfExcelSheet(
    GolfPropertyEnum.playerAllocationBlock,
    GolfExcelSheetName.PlayerTypeMapping,
    [
      golfExcelColumn(
        GolfExcelHeader.AllocationBlockName,
        GolfExcelSourceField.AllocationBlockCode,
      ),
      golfExcelColumn(
        GolfExcelHeader.PlayerTypes,
        GolfExcelSourceField.PlayerType,
      ),
    ],
    GolfExcelSheetName.PlayerTypeMapping,
    mapPlayerTypeMappingRows,
  ),

  golfExcelSheet(
    GolfPropertyEnum.retailSubCategories,
    GolfExcelSheetName.SubCategory,
    [
      golfExcelColumn(
        GolfExcelHeader.Id,
        ExcelEmptyDefault.Empty,
        ExcelEmptyDefault.Empty,
      ),
      golfExcelColumn(
        GolfExcelHeader.Name,
        GolfExcelSourceField.SubCategoryName,
      ),
      golfExcelColumn(
        GolfExcelHeader.Level,
        GolfExcelSourceField.SubCategoryNumber,
        1,
      ),
      golfExcelColumn(
        GolfExcelHeader.IsActive,
        GolfExcelSourceField.Active,
        ExcelBooleanString.True,
      ),
    ],
  ),
];
